/**
 * Spec 028 (US7, D6): units on the services site. Same limits as the backend
 * `services/utils/service-units.ts`. A worker's radius, farthest distance and
 * price per unit are in the worker's `distanceUnit`; a home's area and the
 * large-home threshold are in their own area unit (missing = sq ft).
 */
import {
  AreaUnit,
  asAreaUnit,
  asDistanceUnit,
  convertArea,
  convertDistance,
  DistanceUnit,
  METERS_PER_MILE,
} from './units';

export interface TravelLimits {
  maxRadius: number;
  defaultRadius: number;
  maxTravel: number;
  maxPerUnit: number;
}

export const SERVICE_TRAVEL_LIMITS: Record<DistanceUnit, TravelLimits> = {
  mi: { maxRadius: 100, defaultRadius: 15, maxTravel: 200, maxPerUnit: 20 },
  km: { maxRadius: 160, defaultRadius: 25, maxTravel: 320, maxPerUnit: 12 },
};

export interface AreaLimits {
  minArea: number;
  maxArea: number;
  minLarge: number;
  maxLarge: number;
  defaultLarge: number;
}

export const SERVICE_AREA_LIMITS: Record<AreaUnit, AreaLimits> = {
  sqft: { minArea: 100, maxArea: 20000, minLarge: 300, maxLarge: 20000, defaultLarge: 2000 },
  sqm: { minArea: 10, maxArea: 1860, minLarge: 30, maxLarge: 1860, defaultLarge: 185 },
};

/** "miles" / "mile", or "km". */
export const distanceWord = (unit: DistanceUnit, plural = true): string =>
  unit === 'km' ? 'km' : plural ? 'miles' : 'mile';

/** "sq ft" or "m²", for input suffixes and short text. */
export const areaSuffix = (unit: AreaUnit): string => (unit === 'sqm' ? 'm²' : 'sq ft');

/** "square feet" or "square metres", for messages. */
export const areaWords = (unit: AreaUnit): string =>
  unit === 'sqm' ? 'square metres' : 'square feet';

/** A worker on a metric brand prices homes in square metres too. */
export const areaUnitForDistance = (unit: DistanceUnit): AreaUnit =>
  unit === 'km' ? 'sqm' : 'sqft';

/**
 * Search sends `homeSize.sqft` in square feet (spec D3); a home saved in square
 * metres is converted first.
 */
export const sqftForSearch = (
  value: number | null | undefined,
  unit: AreaUnit | string | null | undefined,
): number | null =>
  value == null || !Number.isFinite(Number(value))
    ? null
    : convertArea(Number(value), asAreaUnit(unit), 'sqft');

/** "1,200 sq ft" or "110 m²". */
export const formatHomeArea = (value: number, unit: AreaUnit | string | null | undefined): string =>
  `${Math.round(value).toLocaleString('en-US')} ${areaSuffix(asAreaUnit(unit))}`;

/** Miles (search answers in miles, D3) in a worker's unit. */
export const milesIn = (miles: number, unit: DistanceUnit): number =>
  convertDistance(miles, 'mi', unit);

/** A worker's radius in metres, for a map circle. */
export const radiusMeters = (
  radius: number,
  unit: DistanceUnit | string | null | undefined,
): number => (asDistanceUnit(unit) === 'km' ? radius * 1000 : radius * METERS_PER_MILE);

/** "5.2 mi away" (today's text) or "8.4 km away". */
export const awayText = (miles: number, unit: DistanceUnit | string | null | undefined): string => {
  const u = asDistanceUnit(unit);
  if (u === 'mi') return `${miles} mi away`;
  return `${Math.round(milesIn(miles, 'km') * 10) / 10} km away`;
};
